import { Box, Button, Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, economic, input } from "../../../../../i18n";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import LabelInput from "../../../../../components/input/LabelInput";
import Grid2 from "@mui/material/Unstable_Grid2";
import { EcoEfficiencyContext } from "../../../../../contexts/EcoEfficiencyContext";
import { PlanSettingPercentChangesTable } from "./PlanSettingPercentChangesTable";
import { PlanSettingRawDataTable } from "./PlanSettingRawDataTable";
import dayjs from "dayjs";
import { EcoEfficiencyService } from "../../../../../service/eco-efficiency.service";
import { CustomComponentRequest } from "../../../../../models/EcoEfficiency";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";
import alertService from "../../../../../components/alert/alertService";
import { PlanSettingNormalizationTable } from "./PlanSettingNormalizationTable";
import {
  ComponentPlan,
  EmissionPlan,
} from "../../../../../models/dto/response/ResponseEcoEfficiency";

export const PlanSetting = (): JSX.Element => {
  const { t } = useTranslation([common, input, economic], {
    nsMode: "fallback",
  });

  const {
    editData,
    setPlanSettingNormalization,
    percentChangesForm,
    rawDataForm,
    getEcoEfficiency,
  } = EcoEfficiencyContext();

  const [isUsePlan, setIsUsePlan] = useState<boolean>(true);

  const [fiveYearForecast, setFiveYearForecast] = useState<number[]>([]);

  const getEcoEfficienciesPlan = async (ecoEfficienciesRefId: string) => {
    let result;
    try {
      result = await EcoEfficiencyService.getEcoEfficienciesPlan(
        ecoEfficienciesRefId,
      );
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      const planSettingNormalization: {
        emissionPlans: EmissionPlan[];
        componentPlans: ComponentPlan[];
      } = {
        emissionPlans: [
          ...result.emissionPlans.map((y) => {
            return {
              _id: y._id,
              id: y.id,
              year: y.year,
              amount: y.amount,
              normalizedValue: y.normalizedValue,
              ecoefficienciesRef: y.ecoefficienciesRef,
              percentChange: y.percentChange,
            };
          }),
        ],
        componentPlans: [...result.componentPlans],
      };

      setPlanSettingNormalization(planSettingNormalization);

      //cal rate type

      result.componentPlans.forEach((c) => {
        let increase = 0;
        let decrease = 0;
        let first = "";

        c.plans.forEach((y, i) => {
          const key = `${c.componentRef}-${y.year}-forecast`;

          y.percentChange >= 0 ? increase++ : decrease++;

          if (i === 0) first = y.percentChange >= 0 ? "increase" : "decrease";

          percentChangesForm.setValue(
            key,
            y.percentChange >= 0 ? y.percentChange : Math.abs(y.percentChange),
            { shouldValidate: false, shouldDirty: false, shouldTouch: false },
          );

          rawDataForm.setValue(key, y.amount, {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false,
          });
        });

        percentChangesForm.setValue(
          `${c.componentRef}-rate-Type`,
          increase === decrease
            ? first
            : increase > decrease
            ? "increase"
            : "decrease",
          { shouldValidate: false, shouldDirty: false, shouldTouch: false },
        );
      });

      result.emissionPlans.forEach((y) => {
        const key = `total-${y.year}-forecast`;
        percentChangesForm.setValue(key, Math.abs(y.percentChange), {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
        rawDataForm.setValue(key, y.amount, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      });
    }

    percentChangesForm.reset(percentChangesForm.getValues(), {
      keepErrors: true,
      keepTouched: true,
    });
    rawDataForm.reset(rawDataForm.getValues(), {
      keepErrors: true,
      keepTouched: true,
    });
  };

  useEffect(() => {
    if (editData) {
      setFiveYearForecast([
        ...[0, 1, 2, 3, 4, 5].map((year) =>
          dayjs(editData?.startDate).add(year, "year").year(),
        ),
      ]);

      getEcoEfficienciesPlan(editData._id);
    }
  }, [editData]);

  useEffect(() => {
    percentChangesForm.reset();
    rawDataForm.reset();
  }, []);

  const onSubmit = async (data: any) => {
    const components: EconomicIndicatorComponent[] =
      editData?.economicindicatorsRef.components.map((c) => c) ?? [];

    const requestBody: CustomComponentRequest = {
      components: components.map((c) => {
        const isUsePercent = percentChangesForm.getValues(`${c._id}-activated`);

        const rateType = percentChangesForm.getValues(`${c._id}-rate-Type`);

        return isUsePercent
          ? {
              componentRef: c._id,
              percentChanges: fiveYearForecast.map((f) => {
                const key = `${c._id}-${f}-forecast`;

                const number: number = Number(
                  percentChangesForm.getValues(key) ?? 0,
                );

                return Number.isNaN(number)
                  ? 0
                  : rateType === "increase"
                  ? number
                  : number * -1;
              }),
            }
          : {
              componentRef: c._id,
              customValue: fiveYearForecast.map((f) => {
                const key = `${c._id}-${f}-forecast`;

                const number: number = data[key] ?? 0;

                return Number.isNaN(number) ? 0 : number;
              }),
            };
      }),
      emissions: percentChangesForm.getValues(`TOTAL-activated`)
        ? {
            percentChanges: fiveYearForecast.map((f) => {
              const key = `total-${f}-forecast`;

              const number: number =
                Number(percentChangesForm.getValues(key) ?? 0) * -1;

              return number;
            }),
          }
        : {
            customValue: fiveYearForecast.map((f) => {
              const key = `total-${f}-forecast`;

              const number: number = Number(data[key] ?? 0);
              return number;
            }),
          },
    };

    let result;
    try {
      result = await EcoEfficiencyService.crateEcoEfficiencyPlans(
        editData!._id,
        requestBody,
      );
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      getEcoEfficienciesPlan(editData!._id);
      getEcoEfficiency();
      alertService.mixin(t("Successfully"));
    }
  };

  const scrollToError = (errors: Record<string, any>) => {
    try {
      const errorFields = Object.keys(errors);

      const errorElement = document.getElementById(
        rawDataForm.formState.isValid ? "raw-data" : "percent",
      );
      errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (error) {
      console.error("Error scrolling to field", error);
    }
  };

  return (
    <>
      <Grid2 container disableEqualOverflow rowSpacing={"20px"}>
        <Grid2 xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                value={isUsePlan}
                onChange={() => setIsUsePlan(!isUsePlan)}
                checked={isUsePlan}
              />
            }
            label={
              <LabelInput
                label={t("Economic-Value-Plan")}
                info={t("Economic-Value-Plan-Description")}
                showRequired={false}
              />
            }
          />
        </Grid2>

        {isUsePlan && (
          <Grid2 xs={12} gap={"20px"} display={"flex"} flexDirection={"column"}>
            {/* percentChanges */}

            {editData?.isBaseYearComplete === true &&
              editData?.year === dayjs(editData?.startDate).year() && (
                <Box id="percent">
                  <PlanSettingPercentChangesTable
                    editData={editData}
                    percentChangesForm={percentChangesForm}
                    rawDataForm={rawDataForm}
                    fiveYearForecast={fiveYearForecast}
                  />
                </Box>
              )}

            {/* rawData */}

            <form onSubmit={rawDataForm.handleSubmit(onSubmit, scrollToError)}>
              <FormProvider {...rawDataForm}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"20px"}
                  id="raw-data"
                >
                  <PlanSettingRawDataTable
                    editData={editData}
                    rawDataForm={rawDataForm}
                    percentChangesForm={percentChangesForm}
                    fiveYearForecast={fiveYearForecast}
                  />

                  <PlanSettingNormalizationTable
                    editData={editData}
                    fiveYearForecast={fiveYearForecast}
                  />
                </Box>

                <Box paddingTop={"24px"} textAlign={"end"}>
                  <Button variant="contained" color="secondary" type="submit">
                    {t("Save")}
                  </Button>
                </Box>
              </FormProvider>
            </form>
          </Grid2>
        )}
      </Grid2>
    </>
  );
};
